<script>
import Multiselect from 'vue-multiselect'
import * as customProfileApi from '@/services/rest-api-sms/lib/custom-profile/custom-profile'

const modalId = 'modal-create-custom-profile'

const createDefaultMapping = () => ({
  device: null,
  sensor: null,
  commandSet: '',
  commandGet: '',
})

const createDefaultForm = () => ({
  company: null,
  profile: null,
  name: '',
  code: '',
  type: 'PRIMARY',
  mappings: [createDefaultMapping()],
})

const typeOptions = ['PRIMARY', 'NON-PRIMARY']

export default {
  components: {
    Multiselect,
  },
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
    deviceTypes: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
    deviceCommands: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      modalId,
      typeOptions,
      mode: 'create',
      selectedProfile: null,
      form: createDefaultForm(),
      profileOptions: [],
      isLoadingProfiles: false,
      isSubmitting: false,
      submitted: false,
      newProfile: true,
    }
  },
  computed: {
    completedMappingCount() {
      return this.form.mappings.filter((mapping) => this.isMappingComplete(mapping)).length
    },
    isSaveDisabled() {
      return this.isSubmitting || this.isFormLocked || !this.isFormValid
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    isFormLocked() {
      return this.isEditMode && !this.selectedProfile
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Custom Profile' : 'Tambah Custom Profile'
    },
    isFormValid() {
      return Boolean(
        this.form.company &&
          this.form.name?.trim() &&
          this.form.code?.trim() &&
          this.form.mappings.length &&
          this.form.mappings.every((mapping) => this.isMappingComplete(mapping))
      )
    },
    checkType() {
      if (this.isEditMode) {
        return !this.selectedProfile || this.newProfile
      } else {
        return this.newProfile
      }
    }
  },
  methods: {
    async show(mode = 'create') {
      this.resetForm()
      this.mode = mode
      this.form.company = null
      this.fetchProfileOptions()
      this.$bvModal.show(modalId)
    },
    resetForm() {
      this.mode = 'create'
      this.selectedProfile = null
      this.form = createDefaultForm()
      this.profileOptions = []
      this.isLoadingProfiles = false
      this.submitted = false
      this.isSubmitting = false
    },
    hideModal() {
      this.$bvModal.hide(modalId)
    },
    customLabelCompany(item) {
      if (!item) return ''
      return `${item.name} (${item.domainName})`
    },
    customLabelDevice(item) {
      if (!item) return ''
      return item.deviceName || item.device_name || '-'
    },
    customLabelSensor(item) {
      if (!item) return ''
      return `${item.sensor_name} (${item.sensor_code})`
    },
    customLabelProfile(item) {
      if (!item) return ''
      if (item.isNew) return item.name
      return `${item.name} (${item.code})`
    },
    customLabelType(type) {
      if (type === 'PRIMARY') return 'Primary'
      if (type === 'NON-PRIMARY') return 'Nonprimary'
      return type || ''
    },
    async fetchProfileOptions() {
      try {
        this.isLoadingProfiles = true
        const companyId = this.getCompanyId(this.form.company)

        let filters = [
          companyId ? `company_id eq ${companyId}`: undefined,
        ]

        const response = await customProfileApi.get({
          limit: 1000,
          filters,
        })

        this.newProfile = true
        this.form.type = "PRIMARY"
        this.profileOptions = this.normalizeProfiles(response)

        if (this.profileOptions.length > 0) {
          this.newProfile = false
        }
      } catch (error) {
        this.handleError(error)
        this.profileOptions = []
      } finally {
        this.isLoadingProfiles = false
      }
    },
    normalizeProfiles(response) {
      if (Array.isArray(response)) return response
      if (Array.isArray(response?.data)) return response.data
      if (Array.isArray(response?.data?.paginated_result)) return response.data.paginated_result
      if (Array.isArray(response?.paginated_result)) return response.paginated_result
      return []
    },
    getCompanyId(company) {
      return Number(company?.id ?? company?.company_id)
    },
    getDeviceCode(device) {
      return Number(device?.deviceCode ?? device?.device_code)
    },
    getSensorCode(sensor) {
      return Number(sensor?.sensor_code)
    },
    getDeviceCodeLabel(mapping) {
      return mapping.device ? this.getDeviceCode(mapping.device) : '-'
    },
    getSensorCodeLabel(mapping) {
      return mapping.sensor ? this.getSensorCode(mapping.sensor) : '-'
    },
    resetMappingCommands(mapping) {
      mapping.commandSet = ''
      mapping.commandGet = ''
    },
    addMapping() {
      if (this.isFormLocked) return
      this.form.mappings.push(createDefaultMapping())
    },
    removeMapping(index) {
      if (this.isFormLocked || this.form.mappings.length <= 1) return
      this.form.mappings.splice(index, 1)
    },
    handleCompanyChange() {
      this.form.profile = null
      this.selectedProfile = null
      this.form.name = ''
      this.form.code = ''
      this.fetchProfileOptions()
    },
    addProfileTag(name) {
      const profile = {
        name,
        code: '',
        isNew: true,
      }

      this.profileOptions = [profile, ...this.profileOptions]
      this.form.profile = profile
      this.handleCreateProfileChange(profile)
    },
    handleCreateProfileChange(profile) {
      this.form.name = profile?.name || ''
      this.form.code = profile?.code || ''
    },
    getDeviceCommandOptions(mapping) {
      if (!this.isMappingReady(mapping)) return []

      const deviceCode = this.getDeviceCode(mapping.device)
      const sensorCode = this.getSensorCode(mapping.sensor)
      const commands = this.deviceCommands
        .filter((command) => {
          return (
            Number(command.device_code) === deviceCode &&
            Number(command.sensor_code) === sensorCode &&
            command.command
          )
        })
        .map((command) => command.command?.trim?.() || command.command)

      return [...new Set(commands)]
    },
    findDeviceOption(deviceCode) {
      return this.deviceTypes.find(
        (device) => Number(device.deviceCode ?? device.device_code) === Number(deviceCode)
      )
    },
    findSensorOption(sensorCode) {
      return this.sensors.find((sensor) => Number(sensor.sensor_code) === Number(sensorCode))
    },
    handleProfileChange(profile) {
      if (!profile) {
        this.form = {
          ...createDefaultForm(),
        }
        return
      }

      this.form = {
        ...this.form,
        name: profile.name || '',
        code: profile.code || '',
        type: profile.type || 'PRIMARY',
        mappings: this.mapProfileCommands(profile.commands || []),
      }
    },
    mapProfileCommands(commands) {
      const groupedCommands = commands.reduce((result, command) => {
        const key = `${command.device_code}-${command.sensor_code}`
        if (!result[key]) {
          result[key] = {
            device: this.findDeviceOption(command.device_code) || null,
            sensor: this.findSensorOption(command.sensor_code) || null,
            commandSet: '',
            commandGet: '',
          }
        }

        if (command.command_type === 'SET') result[key].commandSet = command.command || ''
        if (command.command_type === 'GET') result[key].commandGet = command.command || ''

        return result
      }, {})

      const mappings = Object.values(groupedCommands)
      return mappings.length ? mappings : [createDefaultMapping()]
    },
    isMappingReady(mapping) {
      return Boolean(mapping.device && mapping.sensor)
    },
    isMappingComplete(mapping) {
      return Boolean(
        mapping.device &&
          mapping.sensor &&
          mapping.commandSet?.trim() &&
          mapping.commandGet?.trim()
      )
    },
    getMappingInvalid(mapping, field) {
      if (!this.submitted) return false
      if (field === 'device') return !mapping.device
      if (field === 'sensor') return !mapping.sensor
      if (field === 'commandSet') return !mapping.commandSet?.trim()
      if (field === 'commandGet') return !mapping.commandGet?.trim()
      return false
    },
    getPayload() {
      const commands = this.form.mappings.flatMap((mapping) => {
        const commandBase = {
          device_code: this.getDeviceCode(mapping.device),
          sensor_code: this.getSensorCode(mapping.sensor),
        }

        return [
          {
            ...commandBase,
            command_type: 'SET',
            command: mapping.commandSet.trim(),
          },
          {
            ...commandBase,
            command_type: 'GET',
            command: mapping.commandGet.trim(),
          },
        ]
      })

      return {
        name: this.form.name.trim(),
        code: this.form.code.trim(),
        company_id: this.getCompanyId(this.form.company),
        type: this.form.type,
        commands,
        ...(this.getProfileId(this.form.profile) && !this.isEditMode
          ? { custom_profile_id: this.getProfileId(this.form.profile) }
          : {}),
      }
    },
    getProfileId(profile) {
      return profile?.custom_profile_id || profile?.id
    },
    async submit() {
      this.submitted = true
      if (!this.isFormValid) return

      try {
        this.isSubmitting = true
        const payload = this.getPayload()
        const response = this.isEditMode
          ? await customProfileApi.update(this.getProfileId(this.selectedProfile), payload)
          : await customProfileApi.create(payload)
        if (response.errors) return

        this.$toast.success(this.isEditMode ? 'Data berhasil diubah' : 'Data berhasil tersimpan')
        this.$emit('saved')
        this.hideModal()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmitting = false
      }
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.submit()
    },
    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error || errorData.message)
        }
      } else if (error.request) {
        this.$toast.error(error.response?.statusText || error.message)
      } else {
        this.$toast.error(error.message)
      }
    },
  },
}
</script>

<template>
  <b-modal
    class="mc-modal custom-profile-modal"
    :id="modalId"
    :title="modalTitle"
    size="xl"
    centered
    scrollable
    no-close-on-backdrop
    @hidden="resetForm"
    @ok="handleOk"
  >
    <form class="mc-form custom-profile-modal__form" @submit.stop.prevent="submit">
      <b-row>
        <b-col md="6">
          <b-form-group label="Domain" label-class="required">
            <multiselect
              v-model="form.company"
              :options="companies"
              placeholder="Pilih domain"
              :custom-label="customLabelCompany"
              :allow-empty="false"
              :class="{ 'is-invalid': submitted && !form.company }"
              @input="handleCompanyChange"
            />
            <div v-if="submitted && !form.company" class="invalid-feedback">
              Domain wajib diisi
            </div>
          </b-form-group>
        </b-col>

        <b-col v-if="isEditMode" md="6">
          <b-form-group label="Profile" label-class="required">
            <multiselect
              v-model="selectedProfile"
              :options="profileOptions"
              :disabled="!form.company"
              placeholder="Pilih profile"
              :custom-label="customLabelProfile"
              :allow-empty="false"
              :loading="isLoadingProfiles"
              :class="{ 'is-invalid': submitted && !selectedProfile }"
              @input="handleProfileChange"
            />
            <div v-if="submitted && !selectedProfile" class="invalid-feedback">
              Profile wajib dipilih
            </div>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Type">
            <multiselect
              class="custom-profile-modal__type-select"
              v-model="form.type"
              :options="typeOptions"
              :disabled="checkType"
              :custom-label="customLabelType"
              placeholder="Pilih type"
              :allow-empty="false"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Nama Profile" label-class="required">
            <multiselect
              v-if="!isEditMode"
              v-model="form.profile"
              :options="profileOptions"
              placeholder="Pilih atau masukkan profile baru"
              :custom-label="customLabelProfile"
              :allow-empty="false"
              :taggable="true"
              tag-placeholder="Tambah sebagai profile baru"
              :loading="isLoadingProfiles"
              :disabled="isFormLocked"
              :class="{ 'is-invalid': submitted && !form.name }"
              @tag="addProfileTag"
              @input="handleCreateProfileChange"
            />
            <b-form-input
              v-else
              v-model.trim="form.name"
              placeholder="Masukkan nama profile"
              :disabled="isFormLocked"
              :class="{ 'is-invalid': submitted && !form.name }"
            />
            <div v-if="submitted && !form.name" class="invalid-feedback">
              Nama profile wajib diisi
            </div>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Profile Code" label-class="required">
            <b-form-input
              v-model.trim="form.code"
              placeholder="Masukkan profile code"
              :disabled="isFormLocked"
              :class="{ 'is-invalid': submitted && !form.code }"
            />
            <div v-if="submitted && !form.code" class="invalid-feedback">
              Profile code wajib diisi
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="custom-profile-modal__mapping-header">
        <h6>Mapping perangkat & sensor</h6>
        <div class="custom-profile-modal__mapping-actions">
          <span>{{ completedMappingCount }} dari {{ form.mappings.length }} mapping lengkap</span>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="isFormLocked"
            @click="addMapping"
          >
            <i class="bx bx-plus"></i>
            Tambah Mapping
          </b-button>
        </div>
      </div>

      <div
        v-for="(mapping, index) in form.mappings"
        :key="index"
        class="custom-profile-modal__mapping"
      >
        <div class="custom-profile-modal__mapping-title">
          <h6 class="mb-1">Mapping {{ index + 1 }}</h6>
          <b-button
            variant="outline-danger"
            size="sm"
            :disabled="isFormLocked || form.mappings.length <= 1"
            @click="removeMapping(index)"
          >
            <i class="bx bx-trash-alt"></i>
          </b-button>
        </div>
        <b-row>
          <b-col md="4">
            <b-form-group label="Nama Perangkat">
              <multiselect
                v-model="mapping.device"
                :options="deviceTypes"
                placeholder="Pilih perangkat"
                :custom-label="customLabelDevice"
                :allow-empty="false"
                :disabled="isFormLocked"
                :class="{ 'is-invalid': getMappingInvalid(mapping, 'device') }"
                @input="resetMappingCommands(mapping)"
              />
              <div v-if="getMappingInvalid(mapping, 'device')" class="invalid-feedback">
                Perangkat wajib diisi
              </div>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Kode Perangkat">
              <b-form-input :value="getDeviceCodeLabel(mapping)" disabled />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Nama Sensor">
              <multiselect
                v-model="mapping.sensor"
                :options="sensors"
                placeholder="Pilih sensor"
                :custom-label="customLabelSensor"
                :allow-empty="false"
                :disabled="isFormLocked"
                :class="{ 'is-invalid': getMappingInvalid(mapping, 'sensor') }"
                @input="resetMappingCommands(mapping)"
              />
              <div v-if="getMappingInvalid(mapping, 'sensor')" class="invalid-feedback">
                Sensor wajib diisi
              </div>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Kode Sensor">
              <b-form-input :value="getSensorCodeLabel(mapping)" disabled />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Command Set">
              <multiselect
                v-model="mapping.commandSet"
                :options="getDeviceCommandOptions(mapping)"
                :disabled="isFormLocked || !isMappingReady(mapping)"
                placeholder="Pilih command"
                :allow-empty="false"
                :class="{ 'is-invalid': getMappingInvalid(mapping, 'commandSet') }"
              />
              <div v-if="getMappingInvalid(mapping, 'commandSet')" class="invalid-feedback">
                Command Set wajib diisi
              </div>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Command Get">
              <multiselect
                v-model="mapping.commandGet"
                :options="getDeviceCommandOptions(mapping)"
                :disabled="isFormLocked || !isMappingReady(mapping)"
                placeholder="Pilih command"
                :allow-empty="false"
                :class="{ 'is-invalid': getMappingInvalid(mapping, 'commandGet') }"
              />
              <div v-if="getMappingInvalid(mapping, 'commandGet')" class="invalid-feedback">
                Command Get wajib diisi
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </form>

    <template #modal-footer="{ cancel }">
      <div class="custom-profile-modal__footer">
        <b-button variant="outline-primary" @click="cancel">
          Batal
        </b-button>
        <b-button variant="primary" :disabled="isSaveDisabled" @click="submit">
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          {{ isEditMode ? 'Update' : 'Simpan' }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

